// Чистая логика браузера дебютов (без DOM, покрыта vitest).
// topOpenings — кластеризуем партии юзера по (сторона+дебют), берём топ-N самых
// частых, для каждого реконструируем первые ходы самой частой линии (её проигрывает
// доска), плюс счётчик партий и цвет юзера. Продолжения под доской тянет explorer.
// normalizeExplorer/canonicalUci — приводим рокировку explorer'а (king-takes-rook,
// e1h1) к нотации chess.js (e1g1), иначе тап по O-O из таблицы даёт нелегальный ход.
import { Chess, type Square } from 'chess.js';
import { sideForNick, type CoachGame } from '@ext/core/game-sources';
import type { ExplorerData } from '@ext/core/openings-study';
import { openingLabel as humanOpeningLabel } from '../core/opening-name';

// Lichess explorer кодирует рокировку как «король берёт ладью» (e1h1/e8a8…),
// chess.js — ходом короля (e1g1/e8c8…). Без нормализации ход O-O из таблицы
// продолжений не проигрывается (нелегальный ход в chess.js).
const CASTLE_UCI: Record<string, string> = { e1h1: 'e1g1', e1a1: 'e1c1', e8h8: 'e8g8', e8a8: 'e8c8' };

/** Привести UCI к нотации chess.js: маппим рокировочные пары ТОЛЬКО если с
 * from-клетки действительно ходит король (e1h1 бывает и легальным ходом ладьи). */
export function canonicalUci(fen: string, uci: string): string {
  const mapped = CASTLE_UCI[uci];
  if (!mapped) return uci;
  try {
    const c = new Chess();
    c.load(fen, { skipValidation: true });
    return c.get(uci.slice(0, 2) as Square)?.type === 'k' ? mapped : uci;
  } catch {
    return uci;
  }
}

/** Explorer-ответ с UCI ходов, нормализованными к нотации chess.js. */
export function normalizeExplorer(fen: string, data: ExplorerData): ExplorerData {
  return { ...data, moves: (data.moves || []).map((m) => ({ ...m, uci: canonicalUci(fen, m.uci) })) };
}

/** Название дебюта партии: человекочитаемое (chess.com отдаёт ECOUrl —
 * расшифровываем слаг через core/opening-name, как в списке партий), фолбэк — ECO. */
function openingLabel(game: CoachGame): string {
  return (humanOpeningLabel(game) || game.opening?.eco || '').trim();
}

/** Один полуход реконструированной линии (для проигрывания на доске). */
export interface OpeningLine {
  san: string;
  uci: string;
  parentFen: string; // ДО хода
  fen: string; // ПОСЛЕ хода
}

/** Частый дебют юзера: имя, счётчик партий, цвет + первые ходы главной линии. */
export interface TopOpening {
  key: string;
  name: string;
  count: number; // партий в кластере
  side: 'w' | 'b'; // сторона юзера
  orientation: 'white' | 'black';
  line: OpeningLine[]; // первые ходы самой частой линии кластера
}

interface GameHist {
  game: CoachGame;
  side: 'w' | 'b';
  hist: { color: 'w' | 'b'; from: string; to: string; san: string; before: string; after: string }[];
}

/** Полуходы партии из PGN (chess.js сам режет заголовки/комментарии/[%eval]). */
function historyOf(pgn: string): GameHist['hist'] {
  const c = new Chess();
  try {
    c.loadPgn(pgn);
  } catch {
    return [];
  }
  return c.history({ verbose: true }) as unknown as GameHist['hist'];
}

function signature(hist: GameHist['hist'], plies: number): string {
  return hist
    .slice(0, plies)
    .map((m) => `${m.from}${m.to}`)
    .join(' ');
}


/** Семейство дебюта из полного названия: «Scandinavian Defense Closed» → «Scandinavian Defense»,
 * «Indian Game Black Knights Tango...4.d5 Ne7» → «Indian Game». Обрезаем хвосты-ходы и
 * вариации после маркера семейства. */
export function openingFamily(name: string): string {
  const clean = name.split(/[:,(]|\.\.\./)[0]!.replace(/\d+\.\S*.*$/, '').trim();
  const m = /^(.*?\b(?:Defense|Defence|Opening|Game|Gambit|Attack|System|Variation))\b/.exec(clean);
  return (m ? m[1]! : clean).trim();
}

function openingKey(game: CoachGame, hist: GameHist['hist']): string {
  const named = openingLabel(game);
  // Семейство, а не вариация — иначе 20 партий рассыпаются на кучки по 2-3.
  const family = named ? openingFamily(named) : '';
  return family || signature(hist, 4) || game.id;
}

export interface TopOpeningsOptions {
  limit?: number; // сколько дебютов вернуть (спека: 3)
  linePlies?: number; // глубина проигрываемой линии (спека: 4–6)
  sigPlies?: number; // глубина сигнатуры для выбора представителя кластера
  minPlies?: number; // минимум полуходов, чтобы партия попала в кластер
}

/**
 * Топ-N самых частых дебютов юзера. Кластеризуем по (сторона+дебют), сортируем по
 * числу партий, внутри кластера берём самую частую сигнатуру первых ходов и по её
 * представителю строим линию linePlies полуходов. Пусто, если нет партий с известной
 * стороной юзера.
 */
export function topOpenings(games: CoachGame[], nick: string, opts: TopOpeningsOptions = {}): TopOpening[] {
  const limit = opts.limit ?? 3;
  const linePlies = opts.linePlies ?? 6;
  const sigPlies = opts.sigPlies ?? 6;
  const minPlies = opts.minPlies ?? 2;

  const entries: GameHist[] = [];
  for (const game of games) {
    const side = sideForNick(game, { nick });
    if (!side) continue;
    const hist = historyOf(game.pgn);
    if (hist.length < minPlies) continue;
    entries.push({ game, side, hist });
  }
  if (!entries.length) return [];

  const clusters = new Map<string, GameHist[]>();
  for (const e of entries) {
    const key = `${e.side}|${openingKey(e.game, e.hist)}`;
    (clusters.get(key) || clusters.set(key, []).get(key)!).push(e);
  }

  const ranked = [...clusters.entries()].sort((a, b) => b[1].length - a[1].length).slice(0, limit);

  return ranked.map(([key, group]) => {
    // внутри кластера — самая частая сигнатура первых ходов
    const bySig = new Map<string, GameHist[]>();
    for (const e of group) {
      const sig = signature(e.hist, sigPlies);
      (bySig.get(sig) || bySig.set(sig, []).get(sig)!).push(e);
    }
    let rep: GameHist[] = [];
    for (const g of bySig.values()) if (g.length > rep.length) rep = g;
    const chosen = rep[0]!;

    const count = Math.min(linePlies, chosen.hist.length);
    const line: OpeningLine[] = chosen.hist.slice(0, count).map((m) => ({
      san: m.san,
      uci: `${m.from}${m.to}`,
      parentFen: m.before,
      fen: m.after,
    }));
    const fullName = openingLabel(chosen.game);
    const name = (fullName ? openingFamily(fullName) : '') || line.map((l) => l.san).slice(0, 2).join(' ') || key.replace(/^[wb]\|/, '');

    return { key, name, count: group.length, side: chosen.side, orientation: chosen.side === 'b' ? 'black' : 'white', line };
  });
}
